import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Title1, Subtitle2, Spinner, Button, MessageBar, tokens } from '@fluentui/react-components'
import { Warning20Regular, PeopleRegular } from '@fluentui/react-icons'
import { apiFetchJson } from '../lib/apiFetch'
import { canAccessService, hasAnyAccess, useMe } from '../lib/me'
import { SERVICE_LABELS, INGESTION_BADGE } from '../lib/services'
import { syncFreshnessColor, formatSyncAge, DEFAULT_SYNC_FRESH_DAYS, DEFAULT_SYNC_STALE_DAYS } from '../lib/syncFreshness'
import Lozenge from '../components/Lozenge'
import ServiceIcon from '../components/ServiceIcon'
import type { AppSettings, DashboardResponse, DashboardServiceRow, ServiceConfigResponse } from '../types'

const CARD_STYLE: React.CSSProperties = {
  border: `1px solid ${tokens.colorNeutralStroke2}`,
  borderRadius: tokens.borderRadiusMedium,
  padding: 16,
}

const GRID_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 16,
}

const STAT_LABEL_STYLE: React.CSSProperties = { fontSize: 12, color: tokens.colorNeutralForeground3 }

/**
 * Severity tiers for the Dashboard's problem buttons, from most to least urgent:
 * - danger: an active access risk (a blocked/terminated account still has a live license) — the
 *   core "orphaned license" problem this tool exists to catch.
 * - severe: an unknown identity — could be nothing, could be a real access gap, but it's
 *   unverified either way, one notch below a confirmed risk.
 * - warning: a data-hygiene gap (no owner, a stale override, HR/Entra disagreement) — worth
 *   fixing, not urgent.
 * - informative: a cost/efficiency issue (a wasted duplicate seat), not a security concern at all.
 */
type AlertSeverity = 'danger' | 'severe' | 'warning' | 'informative'

const ALERT_SEVERITY_STYLE: Record<AlertSeverity, React.CSSProperties> = {
  danger: { color: tokens.colorPaletteRedForeground1, borderColor: tokens.colorPaletteRedBorder2 },
  severe: { color: tokens.colorPaletteDarkOrangeForeground1, borderColor: tokens.colorPaletteDarkOrangeBorder2 },
  warning: { color: tokens.colorPaletteMarigoldForeground1, borderColor: tokens.colorPaletteMarigoldBorder2 },
  informative: { color: tokens.colorPaletteBlueForeground2, borderColor: tokens.colorPaletteBlueBorderActive },
}

function AlertButton(props: { severity: AlertSeverity; onClick: () => void; children: React.ReactNode }) {
  return (
    <Button appearance="outline" icon={<Warning20Regular />} onClick={props.onClick} style={ALERT_SEVERITY_STYLE[props.severity]}>
      {props.children}
    </Button>
  )
}

function SeatsCell({ row }: { row: DashboardServiceRow }) {
  if (row.availableSeats === null) {
    return <span>{row.usedCount} used · no seat limit set</span>
  }
  const suffix = row.hasUnlimitedTypes ? ' (+ some unlimited)' : ''
  return (
    <span>
      {row.usedCount} / {row.availableSeats}
      {suffix}
    </span>
  )
}

function CostCell({ row }: { row: DashboardServiceRow }) {
  if (row.estimatedCost === null) return <span style={{ color: tokens.colorNeutralForeground3 }}>—</span>
  const suffix = row.hasFreeTypes ? ' (+ some free/untracked)' : ''
  return (
    <span>
      ${row.estimatedCost.toLocaleString()}
      {suffix}
    </span>
  )
}

export default function Dashboard() {
  const [data, setData] = useState<DashboardResponse | null>(null)
  const [settings, setSettings] = useState<AppSettings | null>(null)
  const [serviceConfig, setServiceConfig] = useState<ServiceConfigResponse['config']>({})
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const me = useMe()

  useEffect(() => {
    Promise.all([
      apiFetchJson<DashboardResponse>('/api/dashboard'),
      apiFetchJson<AppSettings>('/api/settings'),
      apiFetchJson<ServiceConfigResponse>('/api/service-config'),
    ])
      .then(([dashboard, appSettings, config]) => {
        setData(dashboard)
        setSettings(appSettings)
        setServiceConfig(config.config)
      })
      .catch(e => setError(String(e)))
  }, [])

  if (error) return <p>Error: {error}</p>
  if (!data || !settings) return <Spinner label="Loading dashboard..." />

  const freshDays = settings.syncFreshDays ?? DEFAULT_SYNC_FRESH_DAYS
  const staleDays = settings.syncStaleDays ?? DEFAULT_SYNC_STALE_DAYS
  const maxCost = Math.max(0, ...data.services.map(s => s.estimatedCost ?? 0))
  const anyCostTracked = data.services.some(s => s.estimatedCost !== null)
  const showPeopleForce = canAccessService(me, 'peopleforce')
  const showGithub = canAccessService(me, 'github')

  return (
    <div>
      <Title1>Dashboard</Title1>
      <Subtitle2 style={{ display: 'block', marginTop: 4, marginBottom: 24 }}>
        Per-service overview — seats, cost, sync health, and open problems
      </Subtitle2>

      {!hasAnyAccess(me) && (
        <MessageBar intent="warning" style={{ marginBottom: 24 }}>
          You don't have access to any services yet. Ask an administrator to grant it on Settings →
          Access.
        </MessageBar>
      )}

      {/* Identity checks (Users, PeopleForce, GitHub) get their own grid, separate from the
          Licenses grid below — same 3-per-row layout, but keeping the group boundary aligned to a
          row means the two concerns read as distinct without needing a heavy divider. Each card is
          shown only if the caller can see that scope (the directory is admin-only). */}
      {(me.isAdmin || showPeopleForce || showGithub) && (
      <div style={{ ...GRID_STYLE, marginBottom: 24 }}>
        {me.isAdmin && (
        <div style={CARD_STYLE}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <PeopleRegular />
            <Subtitle2>Users</Subtitle2>
            <Lozenge color={INGESTION_BADGE.api.color}>{INGESTION_BADGE.api.label}</Lozenge>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
            <div>
              <div style={STAT_LABEL_STYLE}>Total</div>
              <span>{data.users.totalCount} synced</span>
            </div>
            <div>
              <div style={STAT_LABEL_STYLE}>Last sync</div>
              <Lozenge color={syncFreshnessColor(data.users.lastSyncedAt, freshDays, staleDays)}>
                {formatSyncAge(data.users.lastSyncedAt)}
              </Lozenge>
            </div>
            {data.users.lastSyncErrorCount > 0 && (
              <div>
                <div style={STAT_LABEL_STYLE}>Last sync errors</div>
                <Lozenge color="danger">{data.users.lastSyncErrorCount} errors</Lozenge>
              </div>
            )}
          </div>
        </div>
        )}

        {showPeopleForce && (
        <div style={CARD_STYLE}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <ServiceIcon config={serviceConfig.peopleforce} />
            <Subtitle2>PeopleForce</Subtitle2>
            <Lozenge color={INGESTION_BADGE.api.color}>{INGESTION_BADGE.api.label}</Lozenge>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
            <div>
              <div style={STAT_LABEL_STYLE}>Last sync</div>
              <Lozenge color={syncFreshnessColor(data.peopleForce.lastSyncedAt, freshDays, staleDays)}>
                {formatSyncAge(data.peopleForce.lastSyncedAt)}
              </Lozenge>
            </div>
          </div>
          {(data.peopleForce.lastSyncErrorCount > 0 || data.peopleForce.locationMismatchCount > 0) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {data.peopleForce.lastSyncErrorCount > 0 && (
                <AlertButton severity="danger" onClick={() => navigate('/users/people-force?tab=settings')}>
                  {data.peopleForce.lastSyncErrorCount} errors
                </AlertButton>
              )}
              {data.peopleForce.locationMismatchCount > 0 && (
                <AlertButton severity="warning" onClick={() => navigate('/users/people-force')}>
                  {data.peopleForce.locationMismatchCount} location mismatch
                  {data.peopleForce.locationMismatchCount === 1 ? '' : 'es'}
                </AlertButton>
              )}
            </div>
          )}
        </div>
        )}

        {showGithub && (
        <div style={CARD_STYLE}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <ServiceIcon config={serviceConfig.github} />
            <Subtitle2>GitHub</Subtitle2>
            <Lozenge color={INGESTION_BADGE.api.color}>{INGESTION_BADGE.api.label}</Lozenge>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
            <div>
              <div style={STAT_LABEL_STYLE}>Total</div>
              <span>{data.github.totalCount} members</span>
            </div>
            <div>
              <div style={STAT_LABEL_STYLE}>Last sync</div>
              <Lozenge color={syncFreshnessColor(data.github.lastSyncedAt, freshDays, staleDays)}>
                {formatSyncAge(data.github.lastSyncedAt)}
              </Lozenge>
            </div>
          </div>
          {(data.github.lastSyncErrorCount > 0 || data.github.blockedCount > 0 || data.github.unlinkedCount > 0) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {data.github.lastSyncErrorCount > 0 && (
                <AlertButton severity="danger" onClick={() => navigate('/users/github?tab=settings')}>
                  {data.github.lastSyncErrorCount} errors
                </AlertButton>
              )}
              {data.github.blockedCount > 0 && (
                <AlertButton severity="danger" onClick={() => navigate('/users/github?tab=users&blocked=1')}>
                  {data.github.blockedCount} blocked
                </AlertButton>
              )}
              {data.github.unlinkedCount > 0 && (
                <AlertButton severity="warning" onClick={() => navigate('/users/github?tab=users&unlinked=1')}>
                  {data.github.unlinkedCount} not linked to a person
                </AlertButton>
              )}
            </div>
          )}
        </div>
        )}
      </div>
      )}

      <Subtitle2 style={{ display: 'block', marginBottom: 12 }}>Licenses</Subtitle2>
      <div style={GRID_STYLE}>
        {data.services.map(row => {
          const ingestion = INGESTION_BADGE[row.ingestion]
          const freshness = syncFreshnessColor(row.lastSyncedAt, freshDays, staleDays)
          return (
            <div key={row.serviceId} style={CARD_STYLE}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <ServiceIcon config={serviceConfig[row.serviceId]} />
                <Subtitle2>{SERVICE_LABELS[row.serviceId] ?? row.serviceId}</Subtitle2>
                {ingestion && <Lozenge color={ingestion.color}>{ingestion.label}</Lozenge>}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
                <div>
                  <div style={STAT_LABEL_STYLE}>Seats used / available</div>
                  <SeatsCell row={row} />
                </div>
                <div>
                  <div style={STAT_LABEL_STYLE}>Estimated cost</div>
                  <CostCell row={row} />
                </div>
                <div>
                  <div style={STAT_LABEL_STYLE}>Last sync</div>
                  <Lozenge color={freshness}>{formatSyncAge(row.lastSyncedAt)}</Lozenge>
                </div>
                {row.lastSyncErrorCount > 0 && (
                  <div>
                    <div style={STAT_LABEL_STYLE}>Last sync errors</div>
                    <Lozenge color="danger">{row.lastSyncErrorCount} errors</Lozenge>
                  </div>
                )}
                {row.ownerCount + row.adminCount + row.memberCount > 0 && (
                  <div>
                    <div style={STAT_LABEL_STYLE}>Roles</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {row.ownerCount > 0 && (
                        <Button
                          size="small"
                          appearance="outline"
                          onClick={() => navigate(`/services/${row.serviceId}?tab=licenses&role=Owner`)}
                        >
                          Owner {row.ownerCount}
                        </Button>
                      )}
                      {row.adminCount > 0 && (
                        <Button
                          size="small"
                          appearance="outline"
                          onClick={() => navigate(`/services/${row.serviceId}?tab=licenses&role=Admin`)}
                        >
                          Admin {row.adminCount}
                        </Button>
                      )}
                      {row.memberCount > 0 && (
                        <Button
                          size="small"
                          appearance="outline"
                          onClick={() => navigate(`/services/${row.serviceId}?tab=licenses&role=Member`)}
                        >
                          Member {row.memberCount}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {(row.blockedCount > 0 ||
                row.serviceBlockedCount > 0 ||
                row.unresolvedCount > 0 ||
                row.needsReviewCount > 0 ||
                row.missingOwnerCount > 0 ||
                row.duplicateLicenseCount > 0 ||
                row.notSsoCount > 0 ||
                row.staleLoginCount > 0) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                  {row.blockedCount > 0 && (
                    <AlertButton
                      severity="danger"
                      onClick={() => navigate(`/services/${row.serviceId}?tab=links&problem=blocked`)}
                    >
                      {row.blockedCount} blocked
                    </AlertButton>
                  )}
                  {row.serviceBlockedCount > 0 && (
                    <AlertButton
                      severity="warning"
                      onClick={() => navigate(`/services/${row.serviceId}?tab=licenses&problem=serviceBlocked`)}
                    >
                      {row.serviceBlockedCount} blocked in service
                    </AlertButton>
                  )}
                  {row.unresolvedCount > 0 && (
                    <AlertButton
                      severity="severe"
                      onClick={() => navigate(`/services/${row.serviceId}?tab=links&problem=unresolved`)}
                    >
                      {row.unresolvedCount} unresolved
                    </AlertButton>
                  )}
                  {row.needsReviewCount > 0 && (
                    <AlertButton
                      severity="warning"
                      onClick={() => navigate(`/services/${row.serviceId}?tab=links&problem=needsReview`)}
                    >
                      {row.needsReviewCount} need review
                    </AlertButton>
                  )}
                  {row.missingOwnerCount > 0 && (
                    <AlertButton
                      severity="warning"
                      onClick={() => navigate(`/services/${row.serviceId}?tab=links&problem=missingOwner`)}
                    >
                      {row.missingOwnerCount} missing owner
                    </AlertButton>
                  )}
                  {row.duplicateLicenseCount > 0 && (
                    <AlertButton
                      severity="informative"
                      onClick={() => navigate(`/services/${row.serviceId}?tab=licenses&problem=duplicate`)}
                    >
                      {row.duplicateLicenseCount} duplicate license
                      {row.duplicateLicenseCount === 1 ? '' : 's'}
                    </AlertButton>
                  )}
                  {row.notSsoCount > 0 && (
                    <AlertButton
                      severity="danger"
                      onClick={() => navigate(`/services/${row.serviceId}?tab=licenses&problem=notSso`)}
                    >
                      {row.notSsoCount} not using SSO
                    </AlertButton>
                  )}
                  {row.staleLoginCount > 0 && (
                    <AlertButton
                      severity="warning"
                      onClick={() => navigate(`/services/${row.serviceId}?tab=licenses&problem=stale`)}
                    >
                      {row.staleLoginCount} stale login
                    </AlertButton>
                  )}
                </div>
              )}

              {anyCostTracked && (
                <div style={{ marginTop: 12 }}>
                  <div
                    style={{
                      height: 6,
                      borderRadius: 3,
                      background: tokens.colorNeutralBackground4,
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        borderRadius: 3,
                        background: tokens.colorBrandBackground,
                        width: maxCost > 0 ? `${((row.estimatedCost ?? 0) / maxCost) * 100}%` : '0%',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
